import { useState, useEffect, useCallback } from 'react';
import {
  User,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  fetchSignInMethodsForEmail,
  linkWithCredential,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  OAuthCredential,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { rememberEmailForLink } from '../utils/emailLinkSignIn';
import { clearPageCache } from '../utils/pageCache';
import { markDeviceHasSignedIn } from '../utils/authDeviceHistory';

// Local dev origins (localhost, or a LAN IP when testing from a phone) use
// the popup flow: the redirect flow silently loses its result on origins
// that are cross-origin to the auth domain, since browsers partition the
// auth handler's storage (Safari ITP especially).
const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  /^(?:192\.168\.\d{1,3}\.\d{1,3}|10\.\d{1,3}\.\d{1,3}\.\d{1,3}|172\.(?:1[6-9]|2\d|3[01])\.\d{1,3}\.\d{1,3})$/.test(
    window.location.hostname
  );

// Each useAuth() instance starts its own auth-state listener, but it only
// genuinely needs to resolve once per page load. This flag lets later
// instances (every page remount — e.g. back navigation) initialize as
// already-resolved, so pages render on their first paint instead of waiting
// a render cycle behind `authLoading` again.
let authStateReadyOnce = false;

export type AuthProvider = 'google' | 'twitter' | 'facebook' | 'yahoo' | 'email';

const PENDING_CRED_KEY = 'myl_pending_credential';

const providerDisplayNames: Record<string, string> = {
  'google.com': 'Google',
  'twitter.com': 'X',
  'facebook.com': 'Facebook',
  'yahoo.com': 'Yahoo',
  'password': 'email and password',
};

const storePendingCredential = (credential: OAuthCredential) => {
  sessionStorage.setItem(PENDING_CRED_KEY, JSON.stringify(credential.toJSON()));
};

const retrievePendingCredential = (): OAuthCredential | null => {
  const stored = sessionStorage.getItem(PENDING_CRED_KEY);
  if (!stored) return null;
  sessionStorage.removeItem(PENDING_CRED_KEY);
  try {
    const data = JSON.parse(stored);
    switch (data.providerId) {
      case 'twitter.com':
        return TwitterAuthProvider.credential(data.oauthAccessToken, data.oauthTokenSecret);
      case 'google.com':
        return GoogleAuthProvider.credential(data.oauthIdToken, data.oauthAccessToken);
      case 'facebook.com':
        return FacebookAuthProvider.credential(data.oauthAccessToken);
      default:
        return new OAuthProvider(data.providerId).credential({
          idToken: data.oauthIdToken,
          accessToken: data.oauthAccessToken,
        });
    }
  } catch {
    return null;
  }
};

// Sync user profile with backend after sign-in
const syncUserWithBackend = async (firebaseUser: User): Promise<{ isNewUser: boolean }> => {
  const token = await firebaseUser.getIdToken();
  const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/user/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    throw new Error('Failed to create user profile');
  }
  return response.json();
};

// Stores the colliding credential for later linking and builds the guidance
// message for an account-exists-with-different-credential error.
const accountExistsErrorMessage = async (error: any): Promise<string> => {
  const email = error.customData?.email;
  const credential = OAuthProvider.credentialFromError(error);

  if (credential) {
    storePendingCredential(credential);
  }

  if (email) {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      if (methods.length > 0) {
        const name = providerDisplayNames[methods[0]] || methods[0];
        return `An account already exists with ${email}. Please sign in with ${name} to link your accounts.`;
      }
    } catch { /* fall through to generic message */ }
  }

  return 'An account already exists with this email. Please sign in with your original provider to link your accounts.';
};

// Outcome of the once-per-page-load OAuth redirect check. Firebase hands the
// pending redirect result to a single getRedirectResult call, so it is
// processed once at module scope and the outcome shared with every hook
// instance: whichever mounts first starts the check, instances mounted
// before it settles subscribe, and later instances read it directly.
// Without this only one arbitrary instance would ever see the result, and
// consumers like AuthButton's signup-conversion tracking would miss
// redirect-based sign-ups.
type RedirectOutcome = {
  result: { isNewUser: boolean } | null;
  // Account-linking guidance produced while processing the redirect
  // (account-exists-with-different-credential); surfaced as authError.
  errorMessage: string | null;
};
let redirectOutcome: RedirectOutcome | null = null;
let redirectCheckStarted = false;
const redirectListeners = new Set<(outcome: RedirectOutcome) => void>();

const settleRedirect = (outcome: RedirectOutcome) => {
  redirectOutcome = outcome;
  redirectListeners.forEach((listener) => listener(outcome));
  redirectListeners.clear();
};

const startRedirectCheck = () => {
  if (redirectCheckStarted) return;
  redirectCheckStarted = true;

  if (isLocalhost) {
    // Localhost uses the popup flow — there is never a pending redirect.
    settleRedirect({ result: null, errorMessage: null });
    return;
  }

  getRedirectResult(auth)
    .then(async (result) => {
      if (result?.user) {
        // Link any credential stored by a previous account-exists error.
        const pendingCred = retrievePendingCredential();
        if (pendingCred) {
          await linkWithCredential(result.user, pendingCred);
        }
        const data = await syncUserWithBackend(result.user);
        settleRedirect({ result: { isNewUser: data.isNewUser }, errorMessage: null });
      } else {
        settleRedirect({ result: null, errorMessage: null });
      }
    })
    .catch(async (error) => {
      if (error.code === 'auth/account-exists-with-different-credential') {
        settleRedirect({ result: null, errorMessage: await accountExistsErrorMessage(error) });
      } else {
        console.error('Error handling redirect result:', error);
        settleRedirect({ result: null, errorMessage: null });
      }
    });
};

/**
 * Rewrap a Firebase error with readable copy while keeping `code` intact.
 * Callers need the code to branch on (e.g. falling back from sign-up to
 * sign-in); matching on the message text breaks as soon as the copy changes.
 */
const friendlyAuthError = (error: any): Error => {
  const wrapped = new Error(getErrorMessage(error));
  (wrapped as any).code = error?.code;
  return wrapped;
};

// User-friendly error message mapping
const getErrorMessage = (error: any): string => {
  if (!error?.code) return error.message || 'An unexpected error occurred';

  switch (error.code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-not-found':
      return 'No account found with this email. Try creating an account instead.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again or reset your password.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/invalid-credential':
      return 'Email or password is incorrect. If you don\'t have an account yet, click "Don\'t have an account?" below to create one.';
    case 'auth/invalid-login-credentials':
      return 'Email or password is incorrect. If you don\'t have an account yet, click "Don\'t have an account?" below to create one.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled. Please try a different method.';
    case 'auth/popup-closed-by-user':
    case 'auth/redirect-cancelled-by-user':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/popup-blocked':
      return 'Sign-in was blocked by your browser. Please try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection and try again.';
    default:
      return 'An error occurred during authentication. Please try again.';
  }
};

export const useAuth = () => {
  // auth.currentUser is already populated on remounts (it's only null before
  // Firebase restores the session on a fresh page load), so seeding from it
  // gives pages the signed-in user on their very first render.
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [loading, setLoading] = useState(!(authStateReadyOnce && redirectOutcome !== null));
  const [redirectResult, setRedirectResult] = useState<{ isNewUser: boolean } | null>(
    redirectOutcome?.result ?? null
  );
  const [redirectChecked, setRedirectChecked] = useState(redirectOutcome !== null);
  const [authStateReady, setAuthStateReady] = useState(authStateReadyOnce);
  const [authError, setAuthError] = useState<string | null>(redirectOutcome?.errorMessage ?? null);

  const handleAccountExistsError = useCallback(async (error: any) => {
    setAuthError(await accountExistsErrorMessage(error));
  }, []);

  // After successful sign-in, link any pending credential from a previous
  // account-exists-with-different-credential error.
  const linkPendingCredential = useCallback(async (firebaseUser: User) => {
    const pendingCred = retrievePendingCredential();
    if (pendingCred) {
      await linkWithCredential(firebaseUser, pendingCred);
      setAuthError(null);
    }
  }, []);

  /**
   * Requests our own branded verification email instead of Firebase's. Sends to
   * the authenticated user's own address, so it takes no email parameter.
   */
  const sendVerificationEmail = useCallback(async (firebaseUser: User): Promise<void> => {
    const token = await firebaseUser.getIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/email/verify`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('We just sent one. Check your inbox, or try again in a few minutes.');
      }
      throw new Error('Could not send the email. Please try again.');
    }
    // The email carries a sign-in link, which can only be redeemed alongside
    // the address it was issued for. See utils/emailLinkSignIn.
    if (firebaseUser.email) {
      rememberEmailForLink(firebaseUser.email);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      authStateReadyOnce = true;
      if (user) markDeviceHasSignedIn();
      setUser(user);
      setAuthStateReady(true);
    });

    return unsubscribe;
  }, []);

  // Adopt the shared OAuth redirect outcome (see startRedirectCheck): every
  // instance receives the same result and account-linking error, whether it
  // mounts before or after the check settles.
  useEffect(() => {
    const adopt = (outcome: RedirectOutcome) => {
      setRedirectResult(outcome.result);
      if (outcome.errorMessage) {
        setAuthError(outcome.errorMessage);
      }
      setRedirectChecked(true);
    };

    if (redirectOutcome) {
      adopt(redirectOutcome);
      return;
    }

    redirectListeners.add(adopt);
    startRedirectCheck();
    return () => {
      redirectListeners.delete(adopt);
    };
  }, []);

  // Only mark loading as done once both auth state and redirect check are complete
  useEffect(() => {
    if (authStateReady && redirectChecked) {
      setLoading(false);
    }
  }, [authStateReady, redirectChecked]);

  const createProvider = (providerType: AuthProvider) => {
    switch (providerType) {
      case 'google':
        return new GoogleAuthProvider();
      case 'twitter':
        return new TwitterAuthProvider();
      case 'facebook':
        return new FacebookAuthProvider();
      case 'yahoo':
        const yahooProvider = new OAuthProvider('yahoo.com');
        yahooProvider.addScope('email');
        yahooProvider.addScope('profile');
        return yahooProvider;
      default:
        throw new Error(`Unsupported provider: ${providerType}`);
    }
  };

  const signInWithProvider = async (providerType: AuthProvider) => {
    const provider = createProvider(providerType);
    try {
      if (isLocalhost) {
        // Use popup on localhost — redirect flow doesn't work due to cross-origin
        // restrictions between firebaseapp.com auth handler and localhost.
        const result = await signInWithPopup(auth, provider);
        await linkPendingCredential(result.user);
        const data = await syncUserWithBackend(result.user);
        return { user: result.user, isNewUser: data.isNewUser };
      }

      await signInWithRedirect(auth, provider);
      // The page will redirect to the provider — result is handled
      // in the getRedirectResult useEffect when the user returns.
      return { user: null, isNewUser: false };
    } catch (error: any) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        await handleAccountExistsError(error);
        return { user: null, isNewUser: false };
      }

      console.error(`Error signing in with ${providerType}:`, error);

      if (error.code === 'auth/invalid-credential') {
        console.error(`${providerType} authentication is not properly configured. Please check:
1. ${providerType} provider is enabled in Firebase Console
2. API credentials are correctly set
3. OAuth callback URLs match`);
      }

      throw friendlyAuthError(error);
    }
  };

  // Email/password authentication
  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      await syncUserWithBackend(result.user);
      return result.user;
    } catch (error: any) {
      console.error('Error signing in with email:', error);
      throw friendlyAuthError(error);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      // Sync first so the profile exists before the verification send reads it.
      const data = await syncUserWithBackend(result.user);
      // Non-fatal: the account is already created at this point, so a mail
      // failure must not surface as "sign-up failed". The user can resend from
      // the verification step.
      try {
        await sendVerificationEmail(result.user);
      } catch (error) {
        console.error('Error sending verification email:', error);
      }
      return { user: result.user, isNewUser: data.isNewUser };
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      throw friendlyAuthError(error);
    }
  };

  const resendVerificationEmail = async () => {
    if (user && !user.emailVerified) {
      await sendVerificationEmail(user);
    }
  };

  /**
   * Requests our own branded reset email instead of Firebase's.
   *
   * The endpoint responds 200 whether or not the address has an account, so
   * callers can't distinguish the two and must show a neutral confirmation.
   * Only transport/rate-limit failures reach the catch below.
   */
  const resetPassword = async (email: string) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/auth/email/password-reset`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      }
    );

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many attempts. Please wait a few minutes and try again.');
      }
      throw new Error('Could not send the reset email. Please try again.');
    }
  };

  // Keep backward compatibility
  const signInWithGoogle = () => signInWithProvider('google');

  const logout = async () => {
    try {
      // Cached page data belongs to the signed-in session; drop it so nothing
      // renders for the next account (or on the signed-out landing page).
      clearPageCache();
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return {
    user,
    loading,
    authError,
    redirectResult,
    signInWithProvider,
    signInWithEmail,
    signUpWithEmail,
    resendVerificationEmail,
    resetPassword,
    signInWithGoogle, // Keep for backward compatibility
    logout,
  };
};